import { captureException } from "@sentry/react";
import axios from "axios";
import { create } from "zustand";
import {
  HilosStore,
  InboxParams,
  WebSocketConnectionStatus,
  WebSocketState,
} from "@hilos/types/hilos";
import {
  PLATFORM,
  hasNotificationDevice,
  registerPushyNotification,
  unregisterServiceWorkers,
} from "../helpers/notifications";
import { getUser } from "../helpers/session";
import { API_ROUTES } from "../router/router";

const INITIAL_DEFAULT_INBOX_PARAMS: InboxParams = {
  view: null,
  search: null,
  ordering: "-last_message_on",
  filters: [],
};

const INITIAL_CONNECTION_STATE = {
  status: "disconnected" as const,
  retryCount: 0,
  lastError: null,
};

const INITIAL_WEBSOCKET_STATE: WebSocketState = {
  graphql: { ...INITIAL_CONNECTION_STATE },
  direct: { ...INITIAL_CONNECTION_STATE },
  reconnectTrigger: 0,
};

const MAX_WEBSOCKET_RETRIES = 5;

const useHilosStore = create<HilosStore>((set, get) => ({
  session: null,
  unreadNotificationCount: 0,
  version: null,
  isNetworkOnline: false,
  hasNetworkError: false,
  hasPendingUpdate: false,
  inboxParams: { ...INITIAL_DEFAULT_INBOX_PARAMS },
  templateSyncEvent: null,
  webSocketState: { ...INITIAL_WEBSOCKET_STATE },
  setSession: (nextSession) => {
    const { session: prevSession } = get();

    if (!nextSession || !prevSession) {
      set({ session: nextSession });
      set({ unreadNotificationCount: nextSession.unread_notifications_count });
    }
  },
  loadSession: async () => {
    const user = await getUser();

    if (!user) {
      unregisterServiceWorkers();
      set({ session: null });
      return null;
    }

    try {
      switch (PLATFORM) {
        case "ANDROID":
          // @ts-ignore
          window.UtilityJSBridge.getPushyToken();
          break;
        case "IOS":
          // @ts-ignore
          window.webkit.messageHandlers.UtilityJSBridge.postMessage(
            "getPushyToken"
          );
          break;
        case "BROWSER":
          const hasNotificationDeviceRegistered = await hasNotificationDevice();

          if (!hasNotificationDeviceRegistered) {
            await registerPushyNotification();
          }
          break;
        default:
          break;
      }
    } catch (error) {
      captureException(error);
    }

    set({ session: { ...user } });
    return user;
  },
  updateSession: async (data) => {
    const { session: prevSession } = get();

    if (prevSession && data) {
      const response = await axios.patch(API_ROUTES.ME, data);
      if (response && response.data) {
        const nextSession = {
          ...prevSession,
          ...response.data,
        };
        set({ session: nextSession });
      }
    }
  },
  reloadSession: async () => {
    const { session: prevSession } = get();

    if (prevSession) {
      const user = await getUser();
      const nextSession = {
        ...prevSession,
        ...user,
      };
      set({ session: nextSession });
    }
  },
  setUnreadNotificationCount: (unreadNotificationCount) =>
    set({ unreadNotificationCount }),
  setVersion: (version = null) => set({ version }),
  setPendingUpdate: (hasPendingUpdate = true) => set({ hasPendingUpdate }),
  setIsNetworkOnline: (isNetworkOnline = false) => set({ isNetworkOnline }),
  setHasNetworkError: (hasNetworkError = false) => set({ hasNetworkError }),
  setInboxParams: (nextInboxParams: Partial<InboxParams>) =>
    set(({ inboxParams: prevInboxParams }) => ({
      inboxParams: {
        ...prevInboxParams,
        ...nextInboxParams,
      },
    })),
  setTemplateSyncEvent: (topic) =>
    set({ templateSyncEvent: { topic, timestamp: Date.now() } }),
  setWebSocketStatus: (
    type: "graphql" | "direct",
    status: WebSocketConnectionStatus,
    error?: string | null
  ) =>
    set(({ webSocketState: prevState }) => ({
      webSocketState: {
        ...prevState,
        [type]: {
          ...prevState[type],
          status,
          lastError: error !== undefined ? error : prevState[type].lastError,
          retryCount: status === "connected" ? 0 : prevState[type].retryCount,
        },
      },
    })),
  incrementWebSocketRetry: (type: "graphql" | "direct") =>
    set(({ webSocketState: prevState }) => ({
      webSocketState: {
        ...prevState,
        [type]: {
          ...prevState[type],
          retryCount: Math.min(
            prevState[type].retryCount + 1,
            MAX_WEBSOCKET_RETRIES
          ),
        },
      },
    })),
  resetWebSocketRetry: (type: "graphql" | "direct") =>
    set(({ webSocketState: prevState }) => ({
      webSocketState: {
        ...prevState,
        [type]: {
          ...prevState[type],
          retryCount: 0,
          lastError: null,
        },
      },
    })),
  triggerWebSocketReconnect: () =>
    set(({ webSocketState: prevState }) => ({
      webSocketState: {
        ...prevState,
        direct: { ...INITIAL_CONNECTION_STATE },
        reconnectTrigger: prevState.reconnectTrigger + 1,
      },
    })),
  resetSession: () =>
    set({
      session: null,
      inboxParams: { ...INITIAL_DEFAULT_INBOX_PARAMS },
    }),
  resetInboxParams: () =>
    set({ inboxParams: { ...INITIAL_DEFAULT_INBOX_PARAMS } }),
}));

if (process.env.NODE_ENV !== "production") {
  (window as any).__HILOS_STORE__ = useHilosStore;
}

export default useHilosStore;
